import { toast } from "react-toastify";
import api from "../api/api";
import { startHostedPageCheckout as startHostedPageCheckoutApi } from "../api/api";

let publicKey = null;
let keysLoadPromise = null;
let keysLoadedForTenant = null;

const getTenantId = () => window.location.hostname.split(".")[0];

export const loadApiKeys = async (tenantId) => {
  if (!tenantId) return null;

  if (keysLoadedForTenant === tenantId && publicKey) {
    return { publicKey };
  }

  if (keysLoadPromise && keysLoadedForTenant === tenantId) {
    return keysLoadPromise;
  }

  keysLoadedForTenant = tenantId;
  keysLoadPromise = api
    .get(`/api/checkout/v1/credentials`)
    .then((response) => {
      publicKey = response.data.publicKey;
      return response.data;
    })
    .catch((err) => {
      keysLoadPromise = null;
      keysLoadedForTenant = null;
      publicKey = null;
      throw err;
    });

  return keysLoadPromise;
};

/** Resolves when tenant publishable API credential is available; dedupes concurrent loads. */
export const ensureApiKeysLoaded = async (tenantId = getTenantId()) => {
  if (!tenantId) return false;
  try {
    const data = await loadApiKeys(tenantId);
    return !!data?.publicKey;
  } catch (err) {
    console.error("Failed to load publishable API credential:", err);
    return false;
  }
};

export const createSession = async (
  currency,
  lineItems,
  billingPlatformId,
  couponCodes = []
) => {
  const keysReady = await ensureApiKeysLoaded();
  if (!keysReady) {
    toast.error("Payment system not ready");
    return null;
  }

  const requestBody = {
    paymentData: { currency },
    billingData: {
      billingPlatformId,
      lineItems: lineItems,
      ...(couponCodes?.length ? { couponCodes } : {}),
    },
  };

  try {
    const response = await api.post(
      `/api/checkout/v1/sessions/create`,
      requestBody,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response?.data?.session_id;

  } catch (err) {
    const status = err?.response?.status;

    const backendMessage =
      err?.response?.data?.error ||
      err?.response?.data?.message;

    if (backendMessage) {
      return null;
    }

    if (status === 401) {
      toast.error("Invalid API key");
      return null;
    }

    toast.error("Payment system is temporarily unavailable");
    console.error(err);
    return null;
  }
};

/**
 * Start checkout for a hosted page link. Only the opaque config id is sent;
 * the backend resolves billing platform, line items, coupons and currency
 * server-side (server-authoritative).
 */
export const startHostedPageSession = async (hostedPageConfigId) => {
  const keysReady = await ensureApiKeysLoaded();
  if (!keysReady) {
    toast.error("Payment system not ready");
    return null;
  }

  try {
    const data = await startHostedPageCheckoutApi(hostedPageConfigId);
    if (!data?.session_id) {
      return null;
    }
    return data;
  } catch (err) {
    const status = err?.response?.status;
    const backendMessage =
      err?.response?.data?.error || err?.response?.data?.message;
    if (status === 429) {
      const failure = new Error(
        backendMessage || "Too many checkout attempts. Please wait and try again."
      );
      failure.status = 429;
      failure.backendMessage = failure.message;
      throw failure;
    }
    if (status === 401) {
      toast.error("Invalid API key");
      return null;
    }
    if (backendMessage) {
      const failure = new Error(backendMessage);
      failure.backendMessage = backendMessage;
      failure.status = status;
      throw failure;
    }
    toast.error("Payment system is temporarily unavailable");
    console.error(err);
    return null;
  }
};

/** @deprecated Use {@link startHostedPageSession} — calls the scoped /hosted-pages/{id}/start endpoint. */
export const createSessionFromHostedPage = async (hostedPageConfigId) => {
  const data = await startHostedPageSession(hostedPageConfigId);
  return data?.session_id ?? null;
};

export const updateSession = async (sessionId, payload) => {
    const keysReady = await ensureApiKeysLoaded();
    if (!keysReady) {
        console.error("Public key not loaded yet!");
        return null;
    }

    return api.put(
        `/api/session/v1/update/${sessionId}`,
        payload,
        {
            headers: {
                "Content-Type": "application/json",
                "x-public-key": publicKey,
            },
        }
    );
};


export const fetchSessionDetails = async (sessionId) => {
  if (!sessionId) return null;

  const response = await api.get(`/api/session/v1/${sessionId}`);
  const data = response?.data;
  return {
    dto: data?.checkoutProcessDto || null,
    locale: data?.locale || data?.checkoutProcessDto?.locale || null,
    expiresAt: data?.expires_at,
    expiresAtEpochMs: data?.expires_at_epoch_ms,
    createdAtEpochMs: data?.created_at_epoch_ms,
    remainingSeconds: data?.remaining_seconds,
    origin: data?.origin,
    sessionStatus: data?.session_status,
    paymentMethodPinnedAtCreate: data?.payment_method_pinned_at_create === true,
    pendingPayment: data?.pending_payment
      ? {
          transactionId: data.pending_payment.transaction_id,
          paymentMethod: data.pending_payment.payment_method,
          platform: data.pending_payment.platform,
          paymentResponse: data.pending_payment.payment_response,
        }
      : null,
  };
};

export const fetchSession = async (sessionId) => {
  const details = await fetchSessionDetails(sessionId);
  return details?.dto || null;
};
